"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface RelatedToolsProps {
  tools: Array<{
    title: string;
    shortTitle: string;
    description: string;
    href: string;
    categoryTitle: string;
  }>;
}

/**
 * Grid of related tool cards.
 * 3-column on desktop, 1 on mobile. Links to tool pages.
 */
export function RelatedTools({ tools }: RelatedToolsProps) {
  if (!tools || tools.length === 0) return null;

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
          Related Tools
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Explore more calculators and tools to help with your financial decisions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.map((tool) => (
          <Link
            key={tool.href}
            href={tool.href}
            className="group rounded-lg border border-border p-4 sm:p-5 hover:border-brand/50 hover:bg-secondary/50 transition-all"
          >
            <div className="space-y-3">
              {/* Category badge */}
              <div className="inline-block">
                <span className="inline-flex items-center rounded-full bg-brand/10 px-3 py-1 text-xs font-medium text-brand">
                  {tool.categoryTitle}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-base sm:text-lg font-semibold text-foreground group-hover:text-brand transition-colors line-clamp-2">
                {tool.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-foreground/70 line-clamp-2">
                {tool.description}
              </p>

              {/* Arrow */}
              <div className="flex items-center text-sm font-medium text-brand opacity-0 group-hover:opacity-100 transition-opacity">
                Explore
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
